import React, { useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { Offer } from 'common/contracts';
import { MIN_USD_PRICE } from 'constants/constants';
import {
  BadgesSection,
  GeneralSection,
  IDropdownOption,
  OfferProgressBarSection,
  ProductsSection,
  SaleDiscountSection,
  SegmentsSection,
  TagsSection} from 'views/Offers/components/sections';
import { ICurrentProduct, IProductOption } from 'views/Offers/components/sections/sections.types';
import { IOfferDesignOption } from 'views/Offers/hooks/useOfferDesignOptions';
import { BadgeTypeFilter } from 'views/Offers/Offer.consts';
import { IProductProps, ProductProps } from 'views/Offers/Offer.types';

import Checkbox from 'components/Checkbox/Checkbox';
import { Separator } from 'components/ui/separator';

interface ISettingsTabProps {
  offerDesignOptions: IOfferDesignOption[];
  onOfferDesignChange: (value: string) => void;
  chosenSegment: string[];
  onSegmentsChange: (segments: string[]) => void;
  hasFeatureFlagTags: boolean;
  hasFeatureFlagSetAsFree: boolean;
  isNameTaken: boolean;
  isPublisherOfferIdTaken: boolean;
  isPriceLocalized: boolean;
  sectionItems: IDropdownOption[];
  tabItems: IDropdownOption[];
  enableTabs: boolean;
  relevantBadgeType: BadgeTypeFilter;
  badgeList: IDropdownOption[];
  products: ProductProps[];
  productOptions: IProductOption[];
  currentProduct: ICurrentProduct | null;
  selectedProduct: IProductOption | null;
  onCurrentProductChange: (product: ICurrentProduct | null) => void;
  onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPriorityChange: (value: string) => void;
  onAddProduct: () => void;
  onEditProduct: (product: IProductProps) => void;
  onDeleteProduct: (product: IProductProps) => void;
  onReorderProducts: (products: IProductProps[]) => void;
  progressBarFormValues: { progressBarId: string | null; progressBarPoints: number | null };
  progressBarOffers?: Offer[];
  handleProgressBarFormChange: (...args: any[]) => void;
  onStartHourChange: (hour: string) => void;
}

const SettingsTab: React.FC<ISettingsTabProps> = ({
  offerDesignOptions,
  onOfferDesignChange,
  chosenSegment,
  onSegmentsChange,
  hasFeatureFlagTags,
  hasFeatureFlagSetAsFree,
  isNameTaken,
  isPublisherOfferIdTaken,
  isPriceLocalized,
  sectionItems,
  tabItems,
  enableTabs,
  relevantBadgeType,
  badgeList,
  products,
  productOptions,
  currentProduct,
  selectedProduct,
  onCurrentProductChange,
  onQuantityChange,
  onPriorityChange,
  onAddProduct,
  onEditProduct,
  onDeleteProduct,
  onReorderProducts,
  progressBarFormValues,
  progressBarOffers,
  handleProgressBarFormChange,
  onStartHourChange
}): React.ReactElement => {
  const { setValue, watch } = useFormContext();
  const values = watch();

  const handleSetAsFreeChange = useCallback(
    async (checked: boolean | 'indeterminate'): Promise<void> => {
      const isChecked = checked === true;
      setValue('setAsFree', isChecked, { shouldDirty: true, shouldValidate: true });

      if (isChecked) {
        setValue('price', 0, { shouldDirty: true, shouldValidate: true });
        setValue('playerAvailability', 1, { shouldDirty: true, shouldValidate: true });
      } else {
        onStartHourChange('');
        setValue('price', MIN_USD_PRICE, { shouldDirty: true, shouldValidate: true });
        setValue('playerAvailability', undefined, { shouldDirty: true, shouldValidate: true });
      }
    },
    [setValue, onStartHourChange]
  );

  const handleTagsChange = useCallback(
    (newTags: string[]): void => {
      setValue('tags', newTags, { shouldDirty: true });
    },
    [setValue]
  );

  const isFree = values.setAsFree === true;
  const isPriceEditable = !isFree;

  const priceConfig = useMemo(() => {
    let type: 'number' | 'text' = 'number';
    if (!isPriceEditable) {
      type = 'text';
    }
    return {
      disabled: !isPriceEditable,
      type,
      displayValue: 'Free',
      min: MIN_USD_PRICE
    };
  }, [isPriceEditable]);

  const progressBarInitialValues = useMemo(() => ({
    progressBarId: progressBarFormValues.progressBarId,
    progressBarPoints: progressBarFormValues.progressBarPoints
  }), [progressBarFormValues.progressBarId, progressBarFormValues.progressBarPoints]);

  const isAddDisabled = !currentProduct?.productId || !currentProduct?.quantity;

  return (
    <div className="flex flex-col pb-10">
      <GeneralSection
        offerDesignOptions={offerDesignOptions}
        onOfferDesignChange={onOfferDesignChange}
        isNameTaken={isNameTaken}
        isPublisherOfferIdTaken={isPublisherOfferIdTaken}
        isPriceLocalized={isPriceLocalized}
        sectionItems={sectionItems}
        tabItems={tabItems}
        showSection
        showTabs={enableTabs}
        priceConfig={priceConfig}
        offerDesignListDataTestId="dashPromoDesignList"
        offerDesignDataTestId="offerDesignInpt"
        sectionDataTestId="promotionSectionInput"
      >
        {hasFeatureFlagSetAsFree && (
          <Checkbox
            checked={isFree}
            onCheckedChange={handleSetAsFreeChange}
            label="Set as Free"
          />
        )}
      </GeneralSection>
      <Separator className="my-9 bg-base-border" />
      <SegmentsSection
        items={chosenSegment}
        onItemsChange={onSegmentsChange}
        inputTestId="promoSegmentsInpt"
      />
      <Separator className="my-9 bg-base-border" />
      <BadgesSection
        relevantBadgeType={relevantBadgeType}
        badgeList={badgeList}
      />
      <Separator className="my-9 bg-base-border" />
      <SaleDiscountSection
        showPriceDiscount={values.price > 0}
        showCrossedPrice
        saleLabel="Min Product Sale"
      />
      <Separator className="my-9 bg-base-border" />
      <OfferProgressBarSection
        initialValues={progressBarInitialValues}
        onChange={handleProgressBarFormChange}
        progressBarOffers={progressBarOffers}
      />
      <Separator className="my-5 bg-base-border" />
      <ProductsSection
        productOptions={productOptions}
        currentProduct={currentProduct}
        onCurrentProductChange={onCurrentProductChange}
        onQuantityChange={onQuantityChange}
        onPriorityChange={onPriorityChange}
        onAddProduct={onAddProduct}
        isAddDisabled={isAddDisabled}
        products={products}
        onEditProduct={onEditProduct}
        onDeleteProduct={onDeleteProduct}
        onReorderProducts={onReorderProducts}
        selectedProduct={selectedProduct}
        productListDataTestId="dashPromoProductList"
        priorityListDataTestId="promoMainSubInpt"
      />
      {hasFeatureFlagTags && (
        <React.Fragment>
          <Separator className="my-9 bg-base-border" />
          <TagsSection
            items={values.tags || []}
            onItemsChange={handleTagsChange}
          />
        </React.Fragment>
      )}
    </div>
  );
};

export { SettingsTab };
export default SettingsTab;
